import { createContext, useContext, useMemo, useState, useEffect, useCallback, useRef, use, type ReactNode } from 'react';
import useUser from '@/lib/hooks/use-user';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { ManuscriptService } from '@/data/manuscript/manuscript-service';
import { randomUUID, ensureUser } from '@/lib/utils/utils';
import { AuthContext, DBContext } from '@/lib/context';
import { useManuscripts } from './manuscripts-context';

// Max history steps
const MAX_HISTORY = 50;

import { BUILTIN_MARK_STYLES } from '@/data/manuscript/manuscript-constants';

interface ManuscriptContextType {
    // Data State
    marks: ManuscriptMark[];
    connections: ManuscriptConnection[];
    dividers: ManuscriptDivider[];
    markStyles: ManuscriptMarkStyle[];
    marksByWord: Map<string, ManuscriptMark[]>;
    dividersByWord: Map<string, ManuscriptDivider>;
    wordIndexMap: Map<string, number>;
    loading: boolean;

    activeSelection: { startWordId: string; endWordId: string } | null;
    editingMarkId: string | null;
    draggingSide: 'start' | 'end' | null;
    connectionDraft: { startWordId: string; currentPos: { x: number; y: number } } | null;
    editingConnectionId: string | null;
    activeTab: string;
    showAnnotations: boolean;
    lastUsedStyleId: string | null;
    setLastUsedStyleId: (id: string | null) => void;
    drawModeStyleId: string | null;
    transientHighlights: Set<string>;
    interactionConstraint: InteractionConstraint | null;
    manuallyOpened: boolean;

    // Actions
    setActiveSelection: (selection: { startWordId: string; endWordId: string } | null) => void;
    setEditingMarkId: (id: string | null) => void;
    setDraggingSide: (side: 'start' | 'end' | null) => void;
    setConnectionDraft: (draft: { startWordId: string; currentPos: { x: number; y: number } } | null) => void;
    setEditingConnectionId: (id: string | null) => void;
    setActiveTab: (tab: string) => void;
    isInSelection: (wordId: string) => boolean;
    setDrawModeStyleId: (styleId: string | null) => void;
    setManuallyOpened: (open: boolean) => void;
    updateMarkStyle: (id: string, updates: Partial<ManuscriptMarkStyle>) => void;
    addMarkStyle: (style: Omit<ManuscriptMarkStyle, 'id'>) => string;
    deleteMarkStyle: (id: string) => void;
    ensureMarkStyle: (baseStyleId: string, texture: ManuscriptMarkTexture) => string;

    // Mutation Actions
    addMark: (mark: Omit<ManuscriptMark, 'id' | 'dateCreated' | 'dateUpdated'>) => Promise<string>;
    updateMark: (id: string, updates: Partial<ManuscriptMark>) => Promise<void>;
    deleteMark: (id: string) => Promise<void>;

    addConnection: (conn: Omit<ManuscriptConnection, 'id' | 'dateCreated' | 'dateUpdated'>) => Promise<string>;
    updateConnection: (id: string, updates: Partial<ManuscriptConnection>) => Promise<void>;
    deleteConnection: (id: string) => Promise<void>;

    addDivider: (divider: Omit<ManuscriptDivider, 'id' | 'dateCreated' | 'dateUpdated'>) => Promise<string>;
    deleteDivider: (id: string) => Promise<void>;

    // Utility & Capability API
    setTransientHighlights: (wordIds: string[]) => void;
    findWordsByText: (query: string, options?: FindOptions) => string[];
    scrollToWord: (wordId: string) => void;
    setInteractionConstraint: (constraint: InteractionConstraint | null) => void;
    clearMarksInRange: (startWordId: string, endWordId: string, type?: 'mark' | 'connection' | 'divider' | 'all') => Promise<void>;
    bulkAddMarks: (marks: PredefinedMarkSet) => Promise<void>;
    getMarksByRange: (startWordId: string, endWordId: string) => { marks: ManuscriptMark[], connections: ManuscriptConnection[], dividers: ManuscriptDivider[] };

    // History
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const EMPTY_CONTEXT: ManuscriptContextType = {
    marks: [],
    connections: [],
    dividers: [],
    markStyles: [],
    marksByWord: new Map(),
    dividersByWord: new Map(),
    wordIndexMap: new Map(),
    loading: false,
    activeSelection: null,
    editingMarkId: null,
    draggingSide: null,
    connectionDraft: null,
    editingConnectionId: null,
    showAnnotations: true,
    activeTab: 'highlight',
    lastUsedStyleId: null,
    setLastUsedStyleId: () => { },
    drawModeStyleId: null,
    transientHighlights: new Set(),
    interactionConstraint: null,
    manuallyOpened: false,
    setActiveSelection: () => { },
    setEditingMarkId: () => { },
    setDraggingSide: () => { },
    setConnectionDraft: () => { },
    setEditingConnectionId: () => { },
    setActiveTab: () => { },
    isInSelection: () => false,
    setDrawModeStyleId: () => { },
    setManuallyOpened: () => { },
    updateMarkStyle: () => { },
    addMarkStyle: () => '',
    deleteMarkStyle: () => { },
    ensureMarkStyle: () => '',
    addMark: async () => '',
    updateMark: async () => { },
    deleteMark: async () => { },
    addConnection: async () => '',
    updateConnection: async () => { },
    deleteConnection: async () => { },
    addDivider: async () => '',
    deleteDivider: async () => { },
    setTransientHighlights: () => { },
    findWordsByText: () => [],
    scrollToWord: () => { },
    setInteractionConstraint: () => { },
    clearMarksInRange: async () => { },
    bulkAddMarks: async () => { },
    getMarksByRange: () => ({ marks: [], connections: [], dividers: [] }),
    undo: () => { },
    redo: () => { },
    canUndo: false,
    canRedo: false
};

export interface InteractionConstraint {
    onlyAllow?: string[]; // e.g., ['highlight', 'connect']
    restrictToRange?: { startWordId: string; endWordId: string };
}

export interface FindOptions {
    caseSensitive?: boolean;
    wholeWord?: boolean;
}

export interface PredefinedMarkSet {
    marks?: Omit<ManuscriptMark, 'id' | 'dateCreated' | 'dateUpdated'>[];
    connections?: Omit<ManuscriptConnection, 'id' | 'dateCreated' | 'dateUpdated'>[];
    dividers?: Omit<ManuscriptDivider, 'id' | 'dateCreated' | 'dateUpdated'>[];
}

const ManuscriptContext = createContext<ManuscriptContextType | undefined>(undefined);

export function useManuscript() {
    const context = useContext(ManuscriptContext);
    return context || EMPTY_CONTEXT;
}

interface ProviderProps {
    children: ReactNode;
    passageId: string;
}

interface HistoryState {
    marks: ManuscriptMark[];
    connections: ManuscriptConnection[];
    dividers: ManuscriptDivider[];
}

export function ManuscriptProvider({ children, passageId }: ProviderProps) {
    const { user } = useUser();
    const db = use(DBContext);

    // Unified Data State to prevent race conditions and ensure atomic updates
    const [passageData, setPassageData] = useState<HistoryState>({
        marks: [],
        connections: [],
        dividers: []
    });

    const { activeManuscriptId, activeManuscript, defaultManuscript, updateManuscript, ensureActiveManuscript } = useManuscripts();

    // Convenience destructuring for existing code
    const { marks, connections, dividers } = passageData;
    const markStyles = (activeManuscript?.markStyles && activeManuscript.markStyles.length > 0) ? activeManuscript.markStyles : BUILTIN_MARK_STYLES;

    const [loading, setLoading] = useState(true);

    // History State
    const [history, setHistory] = useState<HistoryState[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const hasInitializedHistory = useRef(false);

    // Word Index (built from DOM, not state)
    const wordIndexMapRef = useRef<Map<string, number>>(new Map());

    // Interaction State
    const [activeSelection, _setActiveSelection] = useState<{ startWordId: string; endWordId: string } | null>(null);
    const [editingMarkId, _setEditingMarkId] = useState<string | null>(null);
    const [draggingSide, setDraggingSide] = useState<'start' | 'end' | null>(null);
    const [connectionDraft, setConnectionDraft] = useState<{ startWordId: string; currentPos: { x: number; y: number } } | null>(null);
    const [editingConnectionId, _setEditingConnectionId] = useState<string | null>(null);
    const { showAnnotations, setShowAnnotations, setHasAnnotations } = usePassageState();
    useEffect(() => {
        setHasAnnotations(marks.length > 0 || dividers.length > 0 || connections.length > 0);
    }, [marks, dividers, connections, setHasAnnotations]);
    const [activeTab, setActiveTab] = useState<string>('highlight');
    const [lastUsedStyleId, setLastUsedStyleId] = useState<string | null>(null);
    const [drawModeStyleId, setDrawModeStyleId] = useState<string | null>(null);
    const [transientHighlights, setTransientHighlightsState] = useState<Set<string>>(new Set());
    const [interactionConstraint, setInteractionConstraint] = useState<InteractionConstraint | null>(null);
    const [manuallyOpened, setManuallyOpened] = useState(false);

    const setActiveSelection = useCallback((selection: { startWordId: string; endWordId: string } | null) => {
        _setActiveSelection(selection);
        if (selection) {
            _setEditingMarkId(null);
            _setEditingConnectionId(null);
        }
    }, []);

    const setEditingMarkId = useCallback((id: string | null) => {
        _setEditingMarkId(id);
        if (id) {
            _setActiveSelection(null);
            setConnectionDraft(null);
            _setEditingConnectionId(null);
        }
    }, []);

    const setEditingConnectionId = useCallback((id: string | null) => {
        _setEditingConnectionId(id);
        if (id) {
            _setActiveSelection(null);
            _setEditingMarkId(null);
            setConnectionDraft(null);
        }
    }, []);

    // Save to Firestore helper
    const persist = useCallback((updatedMarks: ManuscriptMark[], updatedConns: ManuscriptConnection[], updatedDivs: ManuscriptDivider[], manuscriptIdOverride?: string) => {
        const mId = manuscriptIdOverride || activeManuscriptId;
        if (!user || !mId || !passageId || !db) return;

        ManuscriptService.savePassageMarks(db, user.uid, mId, passageId, {
            marks: updatedMarks,
            connections: updatedConns,
            dividers: updatedDivs
        });
    }, [user, activeManuscriptId, passageId, db]);

    // Push to history helper
    const pushHistory = useCallback((newState: HistoryState) => {
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1);
            newHistory.push(newState);
            if (newHistory.length > MAX_HISTORY) {
                newHistory.shift(); // Remove the oldest state
                return newHistory;
            }
            return newHistory;
        });
        setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY - 1));
    }, [historyIndex]);

    // Sync from Firestore
    useEffect(() => {
        if (!user || !passageId || !db || !activeManuscriptId) {
            if (!activeManuscriptId) {
                setPassageData({ marks: [], connections: [], dividers: [] });
            }
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribe = ManuscriptService.subscribeToPassageMarks(
            db,
            user.uid,
            activeManuscriptId,
            passageId,
            (doc) => {
                const newMarks = doc.marks || [];
                const newConns = doc.connections || [];
                const newDivs = doc.dividers || [];

                setPassageData({ marks: newMarks, connections: newConns, dividers: newDivs });

                // Initialize history only once per manuscript switch
                if (!hasInitializedHistory.current) {
                    setHistory([{ marks: newMarks, connections: newConns, dividers: newDivs }]);
                    setHistoryIndex(0);
                    hasInitializedHistory.current = true;
                }
                setLoading(false);
            }
        );

        return () => {
            unsubscribe();
            hasInitializedHistory.current = false; // Reset for next manuscript load
        };
    }, [user, passageId, db, activeManuscriptId]);

    // Update state and history helper
    const applyChange = useCallback((update: HistoryState | ((prev: HistoryState) => HistoryState), manuscriptIdOverride?: string) => {
        setPassageData(prev => {
            const next = typeof update === 'function' ? update(prev) : update;
            pushHistory(next);
            persist(next.marks, next.connections, next.dividers, manuscriptIdOverride);
            return next;
        });
    }, [pushHistory, persist]);

    const auth = use(AuthContext);

    // Mutation implementations
    const addMark = useCallback(async (markData: Omit<ManuscriptMark, 'id' | 'dateCreated' | 'dateUpdated'>) => {
        if (!auth) return '';
        await ensureUser(auth);

        const mId = await ensureActiveManuscript();
        if (!showAnnotations) setShowAnnotations(true);

        const id = randomUUID();
        const now = Date.now();
        const newMark: ManuscriptMark = {
            ...markData,
            id,
            dateCreated: now,
            dateUpdated: now
        };

        applyChange(prev => ({
            ...prev,
            marks: [...prev.marks, newMark]
        }), mId);

        if (markData.markStyleId) {
            setLastUsedStyleId(markData.markStyleId);
        }

        return id;
    }, [auth, ensureActiveManuscript, applyChange]);

    const updateMark = useCallback(async (id: string, updates: Partial<ManuscriptMark>) => {
        const now = Date.now();
        applyChange(prev => ({
            ...prev,
            marks: prev.marks.map(m => m.id === id ? { ...m, ...updates, dateUpdated: now } : m)
        }));
    }, [applyChange]);

    const deleteMark = useCallback(async (id: string) => {
        applyChange(prev => ({
            ...prev,
            marks: prev.marks.filter(m => m.id !== id)
        }));
    }, [applyChange]);

    const addConnection = useCallback(async (connData: Omit<ManuscriptConnection, 'id' | 'dateCreated' | 'dateUpdated'>) => {
        if (!auth) return '';
        await ensureUser(auth);

        const mId = await ensureActiveManuscript();
        if (!showAnnotations) setShowAnnotations(true);

        const id = randomUUID();
        const now = Date.now();
        const newConn: ManuscriptConnection = {
            ...connData,
            id,
            dateCreated: now,
            dateUpdated: now
        };

        applyChange(prev => ({
            ...prev,
            connections: [...prev.connections, newConn]
        }), mId);
        return id;
    }, [auth, ensureActiveManuscript, applyChange]);

    const updateConnection = useCallback(async (id: string, updates: Partial<ManuscriptConnection>) => {
        const now = Date.now();
        applyChange(prev => ({
            ...prev,
            connections: prev.connections.map(c => c.id === id ? { ...c, ...updates, dateUpdated: now } : c)
        }));
    }, [applyChange]);

    const deleteConnection = useCallback(async (id: string) => {
        applyChange(prev => ({
            ...prev,
            connections: prev.connections.filter(c => c.id !== id)
        }));
    }, [applyChange]);

    const addDivider = useCallback(async (divData: Omit<ManuscriptDivider, 'id' | 'dateCreated' | 'dateUpdated'>) => {
        if (!auth) return '';
        await ensureUser(auth);

        const mId = await ensureActiveManuscript();
        if (!showAnnotations) setShowAnnotations(true);

        const id = randomUUID();
        const now = Date.now();
        const newDiv: ManuscriptDivider = {
            ...divData,
            id,
            dateCreated: now,
            dateUpdated: now
        };

        applyChange(prev => {
            // Remove any existing divider on the same word to effectively "update" it
            const filteredDividers = prev.dividers.filter(d => d.wordId !== divData.wordId);
            return {
                ...prev,
                dividers: [...filteredDividers, newDiv]
            };
        }, mId);
        return id;
    }, [auth, ensureActiveManuscript, applyChange]);

    const deleteDivider = useCallback(async (id: string) => {
        applyChange(prev => ({
            ...prev,
            dividers: prev.dividers.filter(d => d.id !== id)
        }));
    }, [applyChange]);

    const updateMarkStyle = useCallback((id: string, updates: Partial<ManuscriptMarkStyle>) => {
        if (!activeManuscriptId) return;
        const newStyles = markStyles.map(s => s.id === id ? { ...s, ...updates } : s);
        updateManuscript(activeManuscriptId, { markStyles: newStyles });
    }, [activeManuscriptId, markStyles, updateManuscript]);

    const addMarkStyle = useCallback((styleData: Omit<ManuscriptMarkStyle, 'id'>) => {
        if (!activeManuscriptId) return '';
        const id = randomUUID();
        const newStyle: ManuscriptMarkStyle = {
            ...styleData,
            id,
            isCustom: true
        };
        updateManuscript(activeManuscriptId, { markStyles: [...markStyles, newStyle] });
        return id;
    }, [activeManuscriptId, markStyles, updateManuscript]);

    const deleteMarkStyle = useCallback((id: string) => {
        if (!activeManuscriptId) return;
        const newStyles = markStyles.filter(s => s.id !== id);
        updateManuscript(activeManuscriptId, { markStyles: newStyles });
    }, [activeManuscriptId, markStyles, updateManuscript]);

    const ensureMarkStyle = useCallback((baseStyleId: string, texture: ManuscriptMarkTexture) => {
        if (!activeManuscriptId) return '';

        // Extract the base color from the ID (handles both 'orange' and 'orange-marker')
        const baseColor = baseStyleId.split('-')[0];
        const targetId = `${baseColor}-${texture}`;

        // If it already exists in our current state, just return it
        const existing = markStyles.find(s => s.id === targetId);
        if (existing) return targetId;

        // Search for a template in this priority order:
        // 1. Saved styles with exact base color match
        // 2. Built-in styles with exact base color match  
        // 3. Built-in styles with base color + any texture (e.g., 'orange-marker')
        // 4. Any built-in highlight style as fallback
        const baseStyle =
            markStyles.find(s => s.id === baseColor) ||
            markStyles.find(s => s.id.startsWith(baseColor + '-')) ||
            BUILTIN_MARK_STYLES.find(s => s.id === baseColor) ||
            BUILTIN_MARK_STYLES.find(s => s.id.startsWith(baseColor + '-')) ||
            BUILTIN_MARK_STYLES.find(s => s.type === 'highlight');

        if (!baseStyle) return '';

        const newStyle: ManuscriptMarkStyle = {
            ...baseStyle,
            id: targetId,
            name: texture === 'marker' ? baseStyle.name : `${baseColor.charAt(0).toUpperCase() + baseColor.slice(1)} (${texture.charAt(0).toUpperCase() + texture.slice(1)})`,
            texture,
            isCustom: true
        };

        updateManuscript(activeManuscriptId, { markStyles: [...markStyles, newStyle] });

        return targetId;
    }, [activeManuscriptId, markStyles, updateManuscript]);

    const allMarkStyles = useMemo(() => {
        const merged = [...markStyles];
        BUILTIN_MARK_STYLES.forEach(builtin => {
            if (!merged.find(s => s.id === builtin.id)) {
                merged.push(builtin);
            }
        });
        return merged;
    }, [markStyles]);

    // Utility & Capability API Methods
    const setTransientHighlights = useCallback((wordIds: string[]) => {
        setTransientHighlightsState(new Set(wordIds));
    }, []);

    const findWordsByText = useCallback((query: string, options: FindOptions = {}) => {
        const results: string[] = [];
        const { caseSensitive, wholeWord } = options;
        const searchStr = caseSensitive ? query : query.toLowerCase();

        const wordElements = document.querySelectorAll('[id^="w-"]');
        wordElements.forEach(el => {
            const text = caseSensitive ? el.textContent || '' : (el.textContent || '').toLowerCase();
            if (wholeWord) {
                const words = text.split(/\s+/);
                if (words.includes(searchStr)) results.push(el.id);
            } else if (text.includes(searchStr)) {
                results.push(el.id);
            }
        });
        return results;
    }, []);

    const scrollToWord = useCallback((wordId: string) => {
        const el = document.getElementById(wordId);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Flash it to show the user where we scrolled
            el.classList.add('animate-pulse');
            setTimeout(() => el.classList.remove('animate-pulse'), 2000);
        }
    }, []);

    const getMarksByRange = useCallback((startWordId: string, endWordId: string) => {
        const wordIndexMap = wordIndexMapRef.current;
        const sIdx = wordIndexMap.get(startWordId);
        const eIdx = wordIndexMap.get(endWordId);
        if (sIdx === undefined || eIdx === undefined) return { marks: [], connections: [], dividers: [] };

        const min = Math.min(sIdx, eIdx);
        const max = Math.max(sIdx, eIdx);

        return {
            marks: marks.filter(m => {
                const ms = wordIndexMap.get(m.startWordId);
                const me = wordIndexMap.get(m.endWordId);
                if (ms === undefined || me === undefined) return false;
                const mMin = Math.min(ms, me);
                const mMax = Math.max(ms, me);
                // True intersection: Mark starts before or at selection end AND ends after or at selection start
                return mMin <= max && mMax >= min;
            }),
            connections: connections.filter(c => {
                const cs = wordIndexMap.get(c.startWordId);
                const ce = wordIndexMap.get(c.endWordId);
                if (cs === undefined || ce === undefined) return false;
                const cMin = Math.min(cs, ce);
                const cMax = Math.max(cs, ce);
                return cMin <= max && cMax >= min;
            }),
            dividers: dividers.filter(d => {
                const di = wordIndexMap.get(d.wordId);
                return di !== undefined && di >= min && di <= max;
            })
        };
    }, [marks, connections, dividers]);

    const clearMarksInRange = useCallback(async (startWordId: string, endWordId: string, type: 'mark' | 'connection' | 'divider' | 'all' = 'all') => {
        const { marks: mInRange, connections: cInRange, dividers: dInRange } = getMarksByRange(startWordId, endWordId);

        const newState = {
            marks: (type === 'mark' || type === 'all') ? marks.filter(m => !mInRange.find(mi => mi.id === m.id)) : marks,
            connections: (type === 'connection' || type === 'all') ? connections.filter(c => !cInRange.find(ci => ci.id === c.id)) : connections,
            dividers: (type === 'divider' || type === 'all') ? dividers.filter(d => !dInRange.find(di => di.id === d.id)) : dividers
        };

        const mId = await ensureActiveManuscript();
        if (!showAnnotations) setShowAnnotations(true);
        applyChange(newState, mId);
    }, [marks, connections, dividers, getMarksByRange, applyChange, ensureActiveManuscript]);

    const bulkAddMarks = useCallback(async (markSet: PredefinedMarkSet) => {
        const now = Date.now();
        const newMarks = (markSet.marks || []).map(m => ({ ...m, id: randomUUID(), dateCreated: now, dateUpdated: now }));
        const newConns = (markSet.connections || []).map(c => ({ ...c, id: randomUUID(), dateCreated: now, dateUpdated: now }));
        const newDivs = (markSet.dividers || []).map(d => ({ ...d, id: randomUUID(), dateCreated: now, dateUpdated: now }));

        const mId = await ensureActiveManuscript();
        if (!showAnnotations) setShowAnnotations(true);

        const newState = {
            marks: [...marks, ...newMarks],
            connections: [...connections, ...newConns],
            dividers: [...dividers, ...newDivs]
        };

        applyChange(newState, mId);
    }, [marks, connections, dividers, applyChange, ensureActiveManuscript]);

    // History Logic
    const undo = useCallback(() => {
        if (historyIndex > 0) {
            const prevState = history[historyIndex - 1];
            setPassageData(prevState);
            setHistoryIndex(prev => prev - 1);
            persist(prevState.marks, prevState.connections, prevState.dividers);
        }
    }, [history, historyIndex, persist]);

    const redo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            const nextState = history[historyIndex + 1];
            setPassageData(nextState);
            setHistoryIndex(prev => prev + 1);
            persist(nextState.marks, nextState.connections, nextState.dividers);
        }
    }, [history, historyIndex, persist]);

    const isInSelection = useCallback((wordId: string, wordIndexMapParam: Map<string, number>) => {
        if (!activeSelection) return false;
        if (activeSelection.startWordId === wordId || activeSelection.endWordId === wordId) return true;

        // Use global word index for accurate cross-verse selection
        const startGlobalIdx = wordIndexMapParam.get(activeSelection.startWordId);
        const endGlobalIdx = wordIndexMapParam.get(activeSelection.endWordId);
        const currentGlobalIdx = wordIndexMapParam.get(wordId);

        if (startGlobalIdx === undefined || endGlobalIdx === undefined || currentGlobalIdx === undefined) {
            return false;
        }

        const min = Math.min(startGlobalIdx, endGlobalIdx);
        const max = Math.max(startGlobalIdx, endGlobalIdx);
        return currentGlobalIdx >= min && currentGlobalIdx <= max;
    }, [activeSelection]);

    // We use a state trigger to force context value update when the ref map changes
    const [indexUpdateCounter, setIndexUpdateCounter] = useState(0);

    // Build word index from DOM
    const buildWordIndex = useCallback(() => {
        const map = new Map<string, number>();
        let globalIndex = 0;

        const wordElements = document.querySelectorAll('[id^="w-"]');
        wordElements.forEach(el => {
            map.set(el.id, globalIndex++);
        });

        wordIndexMapRef.current = map;
        setIndexUpdateCounter(c => c + 1);
    }, []);

    // Rebuild word index when DOM changes (passage rendered/updated)
    useEffect(() => {
        let rafId: number;
        const debouncedBuild = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(buildWordIndex);
        };

        // Initial build
        buildWordIndex();

        // Watch for DOM changes. Only rebuild if bible words are affected.
        const observer = new MutationObserver((mutations) => {
            let shouldRebuild = false;
            for (const mutation of mutations) {
                // Check if any added/removed node is a word or contains words
                const hasWordChanges = Array.from(mutation.addedNodes).some(node =>
                    node instanceof HTMLElement && (node.id.startsWith('w-') || node.querySelector('[id^="w-"]'))
                ) || Array.from(mutation.removedNodes).some(node =>
                    node instanceof HTMLElement && (node.id.startsWith('w-') || node.querySelector('[id^="w-"]'))
                );

                if (hasWordChanges) {
                    shouldRebuild = true;
                    break;
                }
            }

            if (shouldRebuild) {
                debouncedBuild();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });

        // Final fallback: check one more time in a second
        const timer = setTimeout(debouncedBuild, 1000);

        return () => {
            observer.disconnect();
            clearTimeout(timer);
            cancelAnimationFrame(rafId);
        };
    }, [buildWordIndex]);

    // Fast lookup array for word IDs by index
    const wordsByIdx = useMemo(() => {
        const arr = new Array(wordIndexMapRef.current.size);
        wordIndexMapRef.current.forEach((idx, id) => {
            arr[idx] = id;
        });
        return arr;
    }, [indexUpdateCounter]);

    const marksByWord = useMemo(() => {
        const map = new Map<string, ManuscriptMark[]>();
        const wordIndexMap = wordIndexMapRef.current;

        marks.forEach(m => {
            const startIdx = wordIndexMap.get(m.startWordId);
            const endIdx = wordIndexMap.get(m.endWordId);

            if (startIdx === undefined || endIdx === undefined) {
                // Words not yet in DOM, skip for now
                return;
            }

            // Enumerate only words in the range using the pre-built array
            let minIdx = Math.min(startIdx, endIdx);
            let maxIdx = Math.max(startIdx, endIdx);

            // Crossing logic: if start > end while dragging, exclude the pivot word
            if (m.id === editingMarkId && startIdx > endIdx) {
                if (draggingSide === 'start') {
                    minIdx = endIdx + 1;
                } else if (draggingSide === 'end') {
                    maxIdx = startIdx - 1;
                }
            }

            for (let i = minIdx; i <= maxIdx; i++) {
                const wordId = wordsByIdx[i];
                if (wordId) {
                    if (!map.has(wordId)) map.set(wordId, []);
                    map.get(wordId)!.push(m);
                }
            }
        });

        return map;
    }, [marks, wordsByIdx, editingMarkId, draggingSide]); // Depend on wordsByIdx instead of counter

    const dividersByWord = useMemo(() => {
        const map = new Map<string, ManuscriptDivider>();
        dividers.forEach(d => map.set(d.wordId, d));
        return map;
    }, [dividers]);

    const value = useMemo(() => ({
        marks,
        connections,
        dividers,
        marksByWord,
        dividersByWord,
        wordIndexMap: wordIndexMapRef.current,
        loading,
        activeSelection,
        editingMarkId,
        draggingSide,
        connectionDraft,
        editingConnectionId,
        showAnnotations,
        activeTab,
        lastUsedStyleId,
        setLastUsedStyleId,
        drawModeStyleId,
        transientHighlights,
        interactionConstraint,
        manuallyOpened,
        setActiveSelection,
        setEditingMarkId,
        setDraggingSide,
        setConnectionDraft,
        setEditingConnectionId,
        setActiveTab,
        isInSelection: (wordId: string) => isInSelection(wordId, wordIndexMapRef.current),
        setDrawModeStyleId,
        setManuallyOpened,
        markStyles: allMarkStyles,
        updateMarkStyle,
        addMarkStyle,
        deleteMarkStyle,
        ensureMarkStyle,
        addMark,
        updateMark,
        deleteMark,
        addConnection,
        updateConnection,
        deleteConnection,
        addDivider,
        deleteDivider,
        setTransientHighlights,
        findWordsByText,
        scrollToWord,
        setInteractionConstraint,
        clearMarksInRange,
        bulkAddMarks,
        getMarksByRange,
        undo,
        redo,
        canUndo: historyIndex > 0,
        canRedo: historyIndex < history.length - 1
    }), [
        marks, connections, dividers, allMarkStyles, marksByWord, dividersByWord,
        loading, activeSelection, editingMarkId, draggingSide, connectionDraft, editingConnectionId, showAnnotations, activeTab, lastUsedStyleId, drawModeStyleId,
        transientHighlights, interactionConstraint, manuallyOpened,
        setActiveSelection, setEditingMarkId, setDraggingSide, setConnectionDraft, setEditingConnectionId, setActiveTab, isInSelection, setDrawModeStyleId, setManuallyOpened, setLastUsedStyleId, updateMarkStyle, addMarkStyle, deleteMarkStyle, ensureMarkStyle,
        addMark, updateMark, deleteMark, addConnection, updateConnection, deleteConnection,
        addDivider, deleteDivider, setTransientHighlights, findWordsByText, scrollToWord, setInteractionConstraint, clearMarksInRange, bulkAddMarks, getMarksByRange,
        undo, redo, historyIndex, history.length,
        indexUpdateCounter // Important: force update when map changes
    ]);

    return (
        <ManuscriptContext value={value}>
            {children}
        </ManuscriptContext>
    );
}
